import styled from 'styled-components';
import type { SitemapPageViewModel } from '@lib/page-data/sitemap.map';
import type { RequestData } from '@lib/data-fetching';
import type { Locale } from '@lib/locale';
import type { PageInjectedProps } from '../Pages/Page';
import MediaQueryHelper from '../../../templates/MediaQueryHelper';
import SiteMapCategoryGroup from './SiteMapCategoryGroup';
import MaxWidthLayout from '../../../templates/MaxWidthLayout';
import SiteMapShopGroup from './SiteMapShopGroup';
import SiteMapLinksGroup from './SiteMapLinksGroup';
import {
  SITE_MAP_ADDITIONAL_LINKS_SECTION,
  SITE_MAP_CATEGORIES_SECTION,
  SITE_MAP_MAIN_LINKS_SECTION,
  SITE_MAP_STORES_SECTION,
} from '../../../../lib/constants';
import {
  isUserLoggedIn,
} from '../../../../lib/user';
import { extractSiteMapLinks } from '../../../../lib/utils';
import { SiteMapAnchorGroup } from './sitemap.shared';

const PageTitleH1 = styled.h1`
  ${({ theme }) => theme.typographyRebrand.heading['3xl']}
  color: ${(p) => p.theme.c.text.primary};
  text-transform: uppercase;
  margin-bottom: ${(p) => p.theme.unit(-1)};
  margin-top: ${(p) => p.theme.unit(10)};
`;

const SectionGroupDiv = styled.div<{ isLastChild: boolean }>`
  border-bottom: ${(p) => (p.isLastChild ? 'none' : `1px solid ${p.theme.c.border.primary}`)};
  margin-bottom: ${(p) => (p.isLastChild ? p.theme.unit(8) : 'initial')};
`;

const SectionGroupContentDiv = styled.div`
  padding-top: ${(p) => p.theme.unit(5)};
`;

const SectionGroupLinksContentDiv = styled.div`
  padding-top: ${(p) => p.theme.unit(5)};
  padding-bottom: ${(p) => p.theme.unit(5)};
`;

const SectionGroupTitleH2 = styled.h2`
  ${({ theme }) => theme.typographyRebrand.heading['2xl']}
  color: ${({ theme }) => theme.c.text.primary};
  text-transform: uppercase;
  margin-bottom: ${(p) => p.theme.unit(2)};
  margin-top: 0;
`;

type Props = Omit<SitemapPageViewModel, 'pageTitle' | 'metaDescription' | 'allSeasonalLinks'> & {
  seasonalPageData: SitemapPageViewModel['allSeasonalLinks'];
  locale: Locale;
  urlData: RequestData['urlData'];
} & Partial<Pick<PageInjectedProps, 'userData' | 'pageStateModifiers'>>;

const SiteMapPageContent = (props: Props) => {
  const {
    globalPageConfig = {},
    siteMapPageData = {},
    allCategoriesData = [],
    shopOrder = {},
    locale = 'en',
    footersData = {},
    userData = {},
    navCategoryBarData = {},
    seasonalPageData = [],
    pageStateModifiers = {},
    urlData = {},
  } = props;

  const sectionTitle = siteMapPageData?.sectionTitle || [];

  const {
    order = [],
  } = shopOrder;

  const firstRow = footersData?.firstRow || [];
  const secondRow = footersData?.secondRow || [];
  const thirdRow = footersData?.thirdRow || [];

  const firstNavSlots = navCategoryBarData?.firstNavSlots || [];
  const secondNavSlots = navCategoryBarData?.secondNavSlots || [];
  const anonFirstNavSlots = navCategoryBarData?.anonFirstNavSlots || [];
  const anonSecondNavSlots = navCategoryBarData?.anonSecondNavSlots || [];

  const firstNavSlotLinks = isUserLoggedIn(userData)
    ? extractSiteMapLinks(firstNavSlots.filter((slot) => slot !== null))
    : extractSiteMapLinks(anonFirstNavSlots.filter((slot) => slot !== null));

  const secondNavSlotsLinks = isUserLoggedIn(userData)
    ? extractSiteMapLinks(secondNavSlots.filter((slot) => slot !== null))
    : extractSiteMapLinks(anonSecondNavSlots.filter((slot) => slot !== null));

  const allMainLinks = (isMobile: boolean) => [...firstNavSlotLinks, ...secondNavSlotsLinks].map((mainLink) => (
    <SiteMapLinksGroup
      key={mainLink.url || mainLink.name}
      footerLinkData={mainLink}
      locale={locale}
      pageStateModifiers={pageStateModifiers}
      urlData={urlData}
      isMobile={isMobile}
    />
  ));

  // Manipulate seasonal page data to have url to open seasoanl pages
  const seasonalPageDataNew = seasonalPageData
    .filter((page) => page !== null)
    .filter((anySeasonalPage) => anySeasonalPage.active && anySeasonalPage.active === 'true')
    .map((seasonalPage) => ({ ...seasonalPage, url: `seasonal/${seasonalPage.slug}` }));

  const allAdditionalLinks = [...firstRow, ...secondRow, ...thirdRow, ...seasonalPageDataNew]
    .filter((footerLink) => footerLink !== null)
    .map((footerLink) => (
      <SiteMapLinksGroup
        key={footerLink.url || 'unknown'}
        footerLinkData={footerLink}
        locale={locale}
      />
    ));

  const allShopLinks = (isMobile: boolean) => order.map((letter) => (
    <SiteMapShopGroup
      key={`shop-group-${letter}`}
      letter={letter}
      shops={shopOrder[letter]}
      locale={locale}
      isMobile={isMobile}
    />
  ));

  const allCategoryLinks = (isMobile: boolean) => allCategoriesData.map((category) => (
    <SiteMapCategoryGroup
      key={`category-group-${category.name}`}
      category={category}
      locale={locale}
      globalPageConfig={globalPageConfig}
      isMobile={isMobile}
    />
  ));

  const siteMapSectionGroups = (isMobile: boolean) => sectionTitle
    .filter((section) => section !== null)
    .map((section, index, self) => {
      const {
        name = '',
        slug = '',
      } = section;

      const { length } = self;
      const isLastChild = (index === length - 1);

      if (slug === SITE_MAP_MAIN_LINKS_SECTION) {
        return (
          <SectionGroupDiv key={slug} isLastChild={isLastChild}>
            <SectionGroupLinksContentDiv>
              <SectionGroupTitleH2>{name}</SectionGroupTitleH2>
              <SiteMapAnchorGroup>
                {allMainLinks(isMobile)}
              </SiteMapAnchorGroup>
            </SectionGroupLinksContentDiv>
          </SectionGroupDiv>
        );
      }
      if (slug === SITE_MAP_ADDITIONAL_LINKS_SECTION) {
        return (
          <SectionGroupDiv key={slug} isLastChild={isLastChild}>
            <SectionGroupLinksContentDiv>
              <SectionGroupTitleH2>{name}</SectionGroupTitleH2>
              <SiteMapAnchorGroup>
                {allAdditionalLinks}
              </SiteMapAnchorGroup>
            </SectionGroupLinksContentDiv>
          </SectionGroupDiv>
        );
      }
      if (slug === SITE_MAP_STORES_SECTION) {
        return (
          <SectionGroupDiv key={slug} isLastChild={isLastChild}>
            <SectionGroupContentDiv>
              <SectionGroupTitleH2>{name}</SectionGroupTitleH2>
              {allShopLinks(isMobile)}
            </SectionGroupContentDiv>
          </SectionGroupDiv>
        );
      }
      if (slug === SITE_MAP_CATEGORIES_SECTION) {
        return (
          <SectionGroupDiv key={slug} isLastChild={isLastChild}>
            <SectionGroupContentDiv>
              <SectionGroupTitleH2>{name}</SectionGroupTitleH2>
              {allCategoryLinks(isMobile)}
            </SectionGroupContentDiv>
          </SectionGroupDiv>
        );
      }
      return null;
    });

  const displayName = siteMapPageData?.displayName || '';

  const desktopPage = (
    <MaxWidthLayout>
      <PageTitleH1>
        {displayName}
      </PageTitleH1>
      <div>
        {siteMapSectionGroups(false)}
      </div>
    </MaxWidthLayout>
  );

  const mobilePage = (
    <MaxWidthLayout>
      <PageTitleH1>
        {displayName}
      </PageTitleH1>
      <div>
        {siteMapSectionGroups(true)}
      </div>
    </MaxWidthLayout>
  );

  const renderComp = (
    <MediaQueryHelper
      desktopChildren={desktopPage}
      mobileChildren={mobilePage}
    />
  );

  return renderComp;
};

export default SiteMapPageContent;
